import { ComposedChart, Line } from "recharts";
import DashboardExtraCharts from "./DashboardExtraCharts";
import React, { useState, useEffect, useRef } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  RefreshCw,
  Calendar,
  X,
  Package,
  AlertCircle,
  Timer,
  Target,
} from "lucide-react";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DashboardCharts = ({ user }) => {
  const [originalData, setOriginalData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [rawViajes, setRawViajes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Límites configurables (en minutos)
  const TIEMPOS_LIMITE = {
    deposito: 1, // 1 hora
    planta: 4, // 4 horas
    puerto: 1, // 1 hora
  };

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const datePickerRef = useRef(null);

  const [activeFilters, setActiveFilters] = useState({
    // Filtros existentes
    cumplimiento_deposito: null,
    cumplimiento_planta: null,
    cumplimiento_puerto: null,
    tiempo_deposito: null,
    tiempo_planta: null,
    tiempo_puerto: null,
    establecimiento_deposito: null,
    establecimiento_planta: null,
    establecimiento_puerto: null,

    // Filtros nuevos fecha
    fecha_inicio: null,
    fecha_fin: null,

    // 🔹 AGREGAR ESTOS NUEVOS
    establecimiento_transportista: null,
    establecimiento_operador: null,
    tipo_viaje: null,
    mes: null,
    ruta: null,
  });
  const [dateFilters, setDateFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const applyDateFilter = (viajes, startDate, endDate) => {
    if (!startDate && !endDate) return viajes;

    return viajes.filter((viaje) => {
      const viajeDate = new Date(viaje.fecha);

      if (startDate && viajeDate < new Date(startDate)) return false;
      if (endDate && viajeDate > new Date(endDate)) return false;

      return true;
    });
  };

  const applyAllFilters = (viajes) => {
    let filteredViajes = applyDateFilter(
      viajes,
      dateFilters.startDate,
      dateFilters.endDate
    );

    filteredViajes = applyActiveFilters(filteredViajes);

    return filteredViajes;
  };

  useEffect(() => {
    if (originalData && rawViajes.length > 0) {
      const filteredViajes = applyAllFilters(rawViajes);
      const recalculatedData = recalcularMetricas(filteredViajes, user?.tipo);
      setDashboardData(recalculatedData);
    }
  }, [activeFilters, dateFilters, originalData, rawViajes]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
        setShowCalendar(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  useEffect(() => {
    if (originalData && rawViajes.length > 0) {
      const filteredViajes = applyActiveFilters(rawViajes);
      const recalculatedData = recalcularMetricas(filteredViajes, user?.tipo);
      setDashboardData(recalculatedData);
    }
  }, [activeFilters, originalData, rawViajes, dateFilters]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const getApiUrl = () => {
        return process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      };

      const getHeaders = () => {
        const token = localStorage.getItem("token");
        return {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };
      };

      const url = `${getApiUrl()}/dashboard/data`;
      console.log("Cargando datos completos (sin filtros de fecha)");

      const response = await fetch(url, {
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Respuesta del servidor:", result);

      if (result.success) {
        if (result.hasData) {
          setOriginalData(result.data);
          setDashboardData(result.data);

          await fetchRawViajes();
        } else {
          setOriginalData(null);
          setDashboardData(null);
          setRawViajes([]);
          toast(result.message || "No hay datos disponibles", {
            icon: "ℹ️",
          });
        }
      } else {
        setError(result.message || "Error al cargar datos");
        toast.error(result.message || "Error al cargar datos");
      }
    } catch (err) {
      console.error("Error en fetchDashboardData:", err);
      setError("Error de conexión: " + err.message);
      toast.error("Error de conexión al servidor");
    } finally {
      setLoading(false);
    }
  };

  const fetchRawViajes = async () => {
    console.log("Ejemplo de rawViajes:", rawViajes[0]);

    try {
      const getApiUrl = () => {
        return process.env.REACT_APP_API_URL || "http://localhost:8000/api";
      };

      const getHeaders = () => {
        const token = localStorage.getItem("token");
        return {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        };
      };

      const url = `${getApiUrl()}/dashboard/raw-viajes`;

      const response = await fetch(url, {
        headers: getHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setRawViajes(result.data);
        }
      }
    } catch (err) {
      console.error("Error obteniendo viajes individuales:", err);
    }
  };

  const applyActiveFilters = (viajes) => {
    return viajes.filter((viaje) => {
      // ========== FILTROS EXISTENTES (NO TOCAR) ==========
      if (activeFilters.cumplimiento_deposito) {
        const cumple = viaje.tardanzadeposito === 0;
        if (activeFilters.cumplimiento_deposito === "cumple" && !cumple)
          return false;
        if (activeFilters.cumplimiento_deposito === "noCumple" && cumple)
          return false;
      }

      if (activeFilters.cumplimiento_planta) {
        const cumple = viaje.tardanzaplanta === 0;
        if (activeFilters.cumplimiento_planta === "cumple" && !cumple)
          return false;
        if (activeFilters.cumplimiento_planta === "noCumple" && cumple)
          return false;
      }

      if (activeFilters.cumplimiento_puerto) {
        const cumple = viaje.tardanzapuerto === 0;
        if (activeFilters.cumplimiento_puerto === "cumple" && !cumple)
          return false;
        if (activeFilters.cumplimiento_puerto === "noCumple" && cumple)
          return false;
      }

      if (activeFilters.tiempo_deposito) {
        const cumple = viaje.tiempodeposito <= TIEMPOS_LIMITE.deposito * 60;
        if (activeFilters.tiempo_deposito === "cumple" && !cumple) return false;
        if (activeFilters.tiempo_deposito === "noCumple" && cumple)
          return false;
      }

      if (activeFilters.tiempo_planta) {
        const cumple = viaje.tiempoplanta <= TIEMPOS_LIMITE.planta * 60;
        if (activeFilters.tiempo_planta === "cumple" && !cumple) return false;
        if (activeFilters.tiempo_planta === "noCumple" && cumple) return false;
      }

      if (activeFilters.tiempo_puerto) {
        const cumple = viaje.tiempopuerto <= TIEMPOS_LIMITE.puerto * 60;
        if (activeFilters.tiempo_puerto === "cumple" && !cumple) return false;
        if (activeFilters.tiempo_puerto === "noCumple" && cumple) return false;
      }

      if (
        activeFilters.establecimiento_deposito &&
        viaje.deposito !== activeFilters.establecimiento_deposito
      ) {
        return false;
      }

      if (
        activeFilters.establecimiento_planta &&
        viaje.planta !== activeFilters.establecimiento_planta
      ) {
        return false;
      }

      if (
        activeFilters.establecimiento_puerto &&
        viaje.puerto !== activeFilters.establecimiento_puerto
      ) {
        return false;
      }

      // ========== NUEVOS FILTROS PARA GRÁFICOS EXTRAS ==========

      // 🔹 Filtro por Transportista (del PieChart extra)
      if (
        activeFilters.establecimiento_transportista &&
        viaje.transportista !== activeFilters.establecimiento_transportista
      ) {
        return false;
      }

      // 🔹 Filtro por Operador (del PieChart extra)
      if (
        activeFilters.establecimiento_operador &&
        viaje.operador !== activeFilters.establecimiento_operador
      ) {
        return false;
      }
      if (
        activeFilters.tipo_viaje &&
        viaje.viajetipo !== activeFilters.tipo_viaje
      ) {
        return false;
      }

      // Filtro por Mes del Viaje
      if (activeFilters.mes_fecha) {
        let mesViaje = "Sin fecha";
        if (viaje.fecha) {
          let fechaObj;

          // Manejar formato YYYY-MM-DD
          if (/^\d{4}-\d{2}-\d{2}/.test(viaje.fecha)) {
            fechaObj = new Date(viaje.fecha);
          }
          // Manejar formato DD/MM/YYYY
          else if (/^\d{2}\/\d{2}\/\d{4}/.test(viaje.fecha)) {
            const [dia, mesNum, año] = viaje.fecha.split("/");
            fechaObj = new Date(`${año}-${mesNum}-${dia}`);
          }

          if (fechaObj instanceof Date && !isNaN(fechaObj)) {
            // 🔥 USAR MISMO MAPEO QUE DashboardExtraCharts
            const mesesMap = [
              "ene",
              "feb",
              "mar",
              "abr",
              "may",
              "jun",
              "jul",
              "ago",
              "sep",
              "oct",
              "nov",
              "dic",
            ];
            const mesNum = fechaObj.getUTCMonth();
            const mesCorto = mesesMap[mesNum];
            const añoCorto = fechaObj.getUTCFullYear().toString().slice(-2);
            mesViaje = `${mesCorto}-${añoCorto}`;
          }
        }

        if (
          mesViaje.toLowerCase().trim() !==
          activeFilters.mes_fecha?.toLowerCase().trim()
        ) {
          return false;
        }
      }
      // 🔹 Filtro por Ruta (del PieChart de rutas)
      if (activeFilters.ruta_viaje) {
        const deposito = viaje.deposito?.trim().toUpperCase();
        const planta = viaje.planta?.trim().toUpperCase();
        const puerto = viaje.puerto?.trim().toUpperCase();

        const rutaViaje =
          deposito && planta && puerto
            ? `${deposito} - ${planta} - ${puerto}`
            : "NO DEFINIDA";

        if (rutaViaje !== activeFilters.ruta_viaje) {
          return false;
        }
      }

      return true;
    });
  };

  const recalcularMetricas = (viajesFiltrados, userTipo) => {
    const total = viajesFiltrados.length;

    if (total === 0) {
      return {
        cumplimientoDeposito: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En Depot",
          cumplimientoPorEstablecimiento: [],
        },
        cumplimientoPlanta: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En Planta",
          cumplimientoPorEstablecimiento: [],
        },
        cumplimientoPuerto: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En Puerto",
          cumplimientoPorEstablecimiento: [],
        },
        cumplimientoTiempoDeposito: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En Depot",
          totalViajes: 0,
          cumplimientoPorEstablecimiento: [],
        },
        cumplimientoTiempoPlanta: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En cumplimiento_planta",
          totalViajes: 0,
          cumplimientoPorEstablecimiento: [],
        },
        cumplimientoTiempoPuerto: {
          cumple: 0,
          noCumple: 0,
          porcentajeCumple: 0,
          porcentajeNoCumple: 0,
          titulo: "En Puerto",
          totalViajes: 0,
          cumplimientoPorEstablecimiento: [],
        },
        resumenGeneral: originalData?.resumenGeneral || {},
      };
    }

    const cumpleDeposito = viajesFiltrados.filter(
      (v) => v.tardanzadeposito === 0
    ).length;
    const cumplePlanta = viajesFiltrados.filter(
      (v) => v.tardanzaplanta === 0
    ).length;
    const cumplePuerto = viajesFiltrados.filter(
      (v) => v.tardanzapuerto === 0
    ).length;

    const cumpleTiempoDeposito = viajesFiltrados.filter(
      (v) => v.tiempodeposito <= 60
    ).length;
    const cumpleTiempoPlanta = viajesFiltrados.filter(
      (v) => v.tiempoplanta <= 240
    ).length;
    const cumpleTiempoPuerto = viajesFiltrados.filter(
      (v) => v.tiempopuerto <= 60
    ).length;

    // ===== LÓGICA CONDICIONAL: Determinar campo de agrupamiento según tipo de usuario =====
    let campoAgrupamientoDeposito = "deposito";
    let campoAgrupamientoPlanta = "planta";
    let campoAgrupamientoPuerto = "puerto";

    // Para gráficos 4, 5, 6: cambiar agrupamiento según tipo de usuario
    if (userTipo === "operador_logistico") {
      // Operador logístico agrupa por transportista
      campoAgrupamientoDeposito = "transportista";
      campoAgrupamientoPlanta = "transportista";
      campoAgrupamientoPuerto = "transportista";
    } else if (userTipo === "cliente") {
      // Cliente agrupa por operador
      campoAgrupamientoDeposito = "operador";
      campoAgrupamientoPlanta = "operador";
      campoAgrupamientoPuerto = "operador";
    }
    // Si es transportista, mantiene los valores por defecto (deposito, planta, puerto)

    // Gráficos 4, 5, 6: Cumplimiento de cita (agrupamiento dinámico)
    const cumplimientoPorDepositoTardanza = calcularPorEstablecimiento(
      viajesFiltrados,
      campoAgrupamientoDeposito,
      "tardanzadeposito",
      0
    );
    const cumplimientoPorPlantaTardanza = calcularPorEstablecimiento(
      viajesFiltrados,
      campoAgrupamientoPlanta,
      "tardanzaplanta",
      0
    );
    const cumplimientoPorPuertoTardanza = calcularPorEstablecimiento(
      viajesFiltrados,
      campoAgrupamientoPuerto,
      "tardanzapuerto",
      0
    );

    // Gráficos 10, 11, 12: Cumplimiento de tiempo (siempre por establecimiento)
    const cumplimientoPorDepositoTiempo = calcularPorEstablecimiento(
      viajesFiltrados,
      "deposito",
      "tiempodeposito",
      60,
      "<="
    );
    const cumplimientoPorPlantaTiempo = calcularPorEstablecimiento(
      viajesFiltrados,
      "planta",
      "tiempoplanta",
      240,
      "<="
    );
    const cumplimientoPorPuertoTiempo = calcularPorEstablecimiento(
      viajesFiltrados,
      "puerto",
      "tiempopuerto",
      60,
      "<="
    );

    return {
      cumplimientoDeposito: {
        cumple: cumpleDeposito,
        noCumple: total - cumpleDeposito,
        porcentajeCumple: Math.round((cumpleDeposito / total) * 100),
        porcentajeNoCumple: Math.round(
          ((total - cumpleDeposito) / total) * 100
        ),
        titulo: "En Depot",
        cumplimientoPorEstablecimiento: cumplimientoPorDepositoTardanza,
      },
      cumplimientoPlanta: {
        cumple: cumplePlanta,
        noCumple: total - cumplePlanta,
        porcentajeCumple: Math.round((cumplePlanta / total) * 100),
        porcentajeNoCumple: Math.round(((total - cumplePlanta) / total) * 100),
        titulo: "En Planta",
        cumplimientoPorEstablecimiento: cumplimientoPorPlantaTardanza,
      },
      cumplimientoPuerto: {
        cumple: cumplePuerto,
        noCumple: total - cumplePuerto,
        porcentajeCumple: Math.round((cumplePuerto / total) * 100),
        porcentajeNoCumple: Math.round(((total - cumplePuerto) / total) * 100),
        titulo: "En Puerto",
        cumplimientoPorEstablecimiento: cumplimientoPorPuertoTardanza,
      },
      cumplimientoTiempoDeposito: {
        cumple: cumpleTiempoDeposito,
        noCumple: total - cumpleTiempoDeposito,
        porcentajeCumple: Math.round((cumpleTiempoDeposito / total) * 100),
        porcentajeNoCumple: Math.round(
          ((total - cumpleTiempoDeposito) / total) * 100
        ),
        titulo: "En Depot",
        totalViajes: total,
        cumplimientoPorEstablecimiento: cumplimientoPorDepositoTiempo,
      },
      cumplimientoTiempoPlanta: {
        cumple: cumpleTiempoPlanta,
        noCumple: total - cumpleTiempoPlanta,
        porcentajeCumple: Math.round((cumpleTiempoPlanta / total) * 100),
        porcentajeNoCumple: Math.round(
          ((total - cumpleTiempoPlanta) / total) * 100
        ),
        titulo: "En Planta",
        totalViajes: total,
        cumplimientoPorEstablecimiento: cumplimientoPorPlantaTiempo,
      },
      cumplimientoTiempoPuerto: {
        cumple: cumpleTiempoPuerto,
        noCumple: total - cumpleTiempoPuerto,
        porcentajeCumple: Math.round((cumpleTiempoPuerto / total) * 100),
        porcentajeNoCumple: Math.round(
          ((total - cumpleTiempoPuerto) / total) * 100
        ),
        titulo: "En Puerto",
        totalViajes: total,
        cumplimientoPorEstablecimiento: cumplimientoPorPuertoTiempo,
      },
      resumenGeneral: {
        ...originalData?.resumenGeneral,
        totalViajes: total,
      },
    };
  };

  const calcularPorEstablecimiento = (
    viajes,
    campoEstablecimiento,
    campoMetrica,
    valorLimite,
    operador = "==="
  ) => {
    const agrupados = viajes.reduce((acc, viaje) => {
      const establecimiento = viaje[campoEstablecimiento];
      if (!establecimiento) return acc;

      if (!acc[establecimiento]) {
        acc[establecimiento] = [];
      }
      acc[establecimiento].push(viaje);
      return acc;
    }, {});

    return Object.entries(agrupados)
      .map(([establecimiento, viajesEst]) => {
        const total = viajesEst.length;
        let cumple;

        if (operador === "<=") {
          cumple = viajesEst.filter(
            (v) => v[campoMetrica] <= valorLimite
          ).length;
        } else {
          cumple = viajesEst.filter(
            (v) => v[campoMetrica] === valorLimite
          ).length;
        }
        // 🧮 Calcular tiempo promedio
        const sumTiempo = viajesEst.reduce(
          (acc, v) => acc + (v[campoMetrica] || 0),
          0
        );
        const promedio =
          total > 0 ? Math.round((sumTiempo / total) * 100) / 100 : 0;

        return {
          establecimiento,
          porcentaje: Math.round((cumple / total) * 100 * 100) / 100,
          totalViajes: total,
          cumple,
          noCumple: total - cumple,
          promedioTiempo: promedio,
        };
      })
      .sort((a, b) => b.porcentaje - a.porcentaje);
  };

  const handleCrossFilter = (filterType, filterValue, tipo, seccion) => {
    console.log("handleCrossFilter called with:", {
      filterType,
      filterValue,
      tipo,
      seccion,
    });

    // 🔥 Todas las keys deben tener 2 partes
    const filterKey = `${filterType}_${tipo || "general"}`;

    setActiveFilters((prev) => {
      const newFilters = { ...prev };

      if (newFilters[filterKey] === filterValue) {
        newFilters[filterKey] = null;
        toast("Filtro removido", { icon: "🔄" });
      } else {
        newFilters[filterKey] = filterValue;
        const tipoText = tipo.charAt(0).toUpperCase() + tipo.slice(1);

        if (
          ["establecimiento", "mes", "ruta", "tipo_viaje"].includes(filterType)
        ) {
          toast.success(`Filtrado por ${tipoText}: ${filterValue}`);
        } else {
          const cumpleText = filterValue === "cumple" ? "Cumple" : "No Cumple";
          toast.success(`Filtrado ${tipoText}: ${cumpleText}`);
        }
      }

      console.log("New filters:", newFilters);
      return newFilters;
    });
  };

  const clearCrossFilters = () => {
    setActiveFilters({
      cumplimiento_deposito: null,
      cumplimiento_planta: null,
      cumplimiento_puerto: null,
      tiempo_deposito: null,
      tiempo_planta: null,
      tiempo_puerto: null,
      establecimiento_deposito: null,
      establecimiento_planta: null,
      establecimiento_puerto: null,

      // 🔹 AGREGAR ESTOS
      establecimiento_transportista: null,
      establecimiento_operador: null,
      tipo_viaje: null,
      mes: null,
      ruta: null,
    });
    toast.success("Todos los filtros eliminados");
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateISO = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const handleQuickRange = (days) => {
    const today = new Date();
    let startDate, endDate;

    if (days === "today") {
      startDate = today;
      endDate = today;
    } else if (days === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = yesterday;
      endDate = yesterday;
    } else if (days === "thisMonth") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (days === "lastMonth") {
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
    } else {
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - days);
      endDate = today;
    }

    setDateRange({ startDate, endDate });
    setDateFilters({
      startDate: formatDateISO(startDate),
      endDate: formatDateISO(endDate),
    });

    setHasAppliedFilter(true);
    setShowDatePicker(false);
    toast.success("Filtro de fecha aplicado");
  };

  const handleApplyFilters = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.error("Debe seleccionar ambas fechas");
      return;
    }

    setDateFilters({
      startDate: formatDateISO(dateRange.startDate),
      endDate: formatDateISO(dateRange.endDate),
    });

    setHasAppliedFilter(true);
    setShowDatePicker(false);
    toast.success("Filtros de fecha aplicados");
  };

  const clearFilters = () => {
    setDateRange({ startDate: null, endDate: null });
    setHasAppliedFilter(false);
    setShowCalendar(false);
    clearCrossFilters();
    fetchDashboardData();
    setShowDatePicker(false);
    toast.success("Todos los filtros eliminados");
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("es-PE").format(num);
  };

  const COLORS = {
    cumple: "#3B82F6",
    noCumple: "#EF4444",
  };

  const preparePieData = (metricas) => {
    return [
      { name: "SÍ", value: metricas.cumple, color: COLORS.cumple },
      { name: "NO", value: metricas.noCumple, color: COLORS.noCumple },
    ];
  };

  const prepareBarData = (establecimientos, isTracto = false) => {
    if (!establecimientos || establecimientos.length === 0) return [];
    return establecimientos.map((est) => ({
      [isTracto ? "tracto" : "establecimiento"]: isTracto
        ? est.tracto
        : est.establecimiento,
      porcentaje: est.porcentaje,
      totalViajes: est.totalViajes,
      promedioTiempo: est.promedioTiempo,
    }));
  };

  const PieChartWithLegend = ({
    data,
    title,
    total,
    tipo,
    seccion,
    icon: Icon = Package,
  }) => {
    const handlePieClick = (entry) => {
      const filterValue = entry.name === "SÍ" ? "cumple" : "noCumple";
      console.log("Pie clicked:", { seccion, tipo, filterValue });

      const filterSection = seccion === "tiempo" ? "tiempo" : "cumplimiento";
      handleCrossFilter(filterSection, filterValue, tipo, seccion);
    };

    const filterSection = seccion === "tiempo" ? "tiempo" : "cumplimiento";
    const hasActiveFilter = activeFilters[`${filterSection}_${tipo}`];

    return (
      <div
        className={`bg-white p-3 rounded-lg shadow-sm border ${
          hasActiveFilter ? "ring-2 ring-blue-400" : ""
        }`}
      >
        <div className="flex items-center space-x-2 mb-2">
          <Icon className="h-4 w-4 text-blue-600" />
          <h3 className="text-[14px] font-semibold text-gray-700 text-center leading-tight px-2">
            {title}
          </h3>
        </div>

        <div className="w-full h-44 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                outerRadius={55}
                innerRadius={0}
                dataKey="value"
                onClick={(entry) => handlePieClick(entry)}
                className="cursor-pointer"
                label={({
                  value,
                  percent,
                  cx,
                  cy,
                  midAngle,
                  outerRadius,
                  index,
                }) => {
                  const RADIAN = Math.PI / 180;
                  const labelRadius = outerRadius + 20;
                  const x = cx + labelRadius * Math.cos(-midAngle * RADIAN);
                  const y = cy + labelRadius * Math.sin(-midAngle * RADIAN);
                  const x1 =
                    cx + (outerRadius + 3) * Math.cos(-midAngle * RADIAN);
                  const y1 =
                    cy + (outerRadius + 3) * Math.sin(-midAngle * RADIAN);
                  const textAnchor = x > cx ? "start" : "end";

                  return (
                    <g>
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x}
                        y2={y}
                        stroke="#6B7280"
                        strokeWidth={1}
                      />
                      <text
                        x={x}
                        y={y}
                        fill="#374151"
                        textAnchor={textAnchor}
                        dominantBaseline="central"
                        fontSize={10}
                        fontWeight="600"
                      >
                        {`${formatNumber(value)} (${(percent * 100).toFixed(
                          0
                        )}%)`}
                      </text>
                    </g>
                  );
                }}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  color: "#374151",
                  fontSize: "12px",
                  padding: "8px 12px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value) => [`${formatNumber(value)} viajes`]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            {data.map((entry, index) => (
              <div
                key={index}
                className="flex items-center text-xs cursor-pointer hover:bg-gray-100 p-1 rounded transition-colors"
                onClick={() => handlePieClick(entry)}
              >
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="font-medium">{entry.name}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 font-medium">
            Total: {formatNumber(total)} viajes
          </div>
        </div>
      </div>
    );
  };

  const BarChartCard = ({
    establecimientos,
    tipo,
    seccion,
    isTracto = false,
  }) => {
    const barData = prepareBarData(establecimientos, isTracto);
    const dataKey = isTracto ? "tracto" : "establecimiento";

    const handleBarClick = (data, index) => {
      if (data && data[dataKey]) {
        handleCrossFilter("establecimiento", data[dataKey], tipo, seccion);
      }
    };

    const activeEstablecimiento = activeFilters[`establecimiento_${tipo}`];
    const hasActiveFilter = activeEstablecimiento !== null;

    if (!barData || barData.length === 0) {
      return (
        <div className="bg-gray-50 p-3 rounded-lg border">
          <h4 className="text-xs font-medium text-gray-600 mb-2 text-center">
            Cumplimiento por {isTracto ? "Camión" : "Establecimiento"} - {tipo}
          </h4>
          <p className="text-xs text-gray-500 text-center">
            Sin datos disponibles
          </p>
        </div>
      );
    }

    // 🔹 Verificamos si los datos tienen promedioTiempo (solo en gráficos de tiempo)
    const hasPromedio = barData.some((d) => d.promedioTiempo !== undefined);

    return (
      <div
        className={`bg-white p-3 rounded-lg shadow-sm border ${
          hasActiveFilter ? "ring-2 ring-blue-400" : ""
        }`}
      >
        <h4 className="text-xs font-medium text-gray-700 mb-3 text-center">
          Cumplimiento por {isTracto ? "Camión" : "Establecimiento"} - {tipo}
        </h4>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {seccion === "tiempo" && hasPromedio ? (
              // 🔸 Gráfico mixto (barras + línea)
              <ComposedChart
                data={barData}
                margin={{ top: 15, right: 15, left: 15, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey={dataKey}
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => `${value} min`}
                  domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    color: "#374151",
                    fontSize: "11px",
                    padding: "6px 10px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  formatter={(value, name) =>
                    name === "porcentaje"
                      ? [`${value}%`, "Cumplimiento"]
                      : [`${value} min`, "Tiempo promedio"]
                  }
                />
                <Bar yAxisId="left" dataKey="porcentaje" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, index) => {
                    const isSelected = activeEstablecimiento === entry[dataKey];
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={isSelected ? "#FCD34D" : "#3B82F6"}
                        onClick={() => handleBarClick(entry, index)}
                        style={{
                          cursor: "pointer",
                          stroke: isSelected ? "#F59E0B" : "none",
                          strokeWidth: isSelected ? 2 : 0,
                        }}
                        className="hover:opacity-80 transition-all duration-200"
                      />
                    );
                  })}
                </Bar>

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="promedioTiempo"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  strokeOpacity={0} // Oculta la línea
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            ) : (
              // 🔸 Gráfico de barras simple (como antes)
              <BarChart
                data={barData}
                margin={{ top: 15, right: 15, left: 15, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey={dataKey}
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    color: "#374151",
                    fontSize: "11px",
                    padding: "6px 10px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  formatter={(value) => [`${value}%`, "Cumplimiento"]}
                />
                <Bar dataKey="porcentaje" radius={[4, 4, 0, 0]}>
                  {barData.map((entry, index) => {
                    const isSelected = activeEstablecimiento === entry[dataKey];
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          isSelected
                            ? "#FCD34D"
                            : isTracto
                            ? "#8B5CF6"
                            : "#3B82F6"
                        }
                        onClick={() => handleBarClick(entry, index)}
                        style={{
                          cursor: "pointer",
                          stroke: isSelected ? "#F59E0B" : "none",
                          strokeWidth: isSelected ? 2 : 0,
                        }}
                        className="hover:opacity-80 transition-all duration-200"
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Mostrar establecimiento seleccionado */}
        {hasActiveFilter && (
          <div className="mt-2 text-center">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Filtrado: {activeEstablecimiento}
              <button
                onClick={() =>
                  handleCrossFilter(
                    "establecimiento",
                    activeEstablecimiento,
                    tipo,
                    seccion
                  )
                }
                className="ml-1 hover:text-yellow-600"
              >
                ×
              </button>
            </span>
          </div>
        )}
      </div>
    );
  };

  const ActiveFiltersIndicator = () => {
    const activeFiltersList = Object.entries(activeFilters).filter(
      ([key, value]) => value !== null
    );
    const hasDateFilter = dateFilters.startDate || dateFilters.endDate;

    if (activeFiltersList.length === 0 && !hasDateFilter) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-blue-900">
              Filtros activos:
            </span>

            {/* Filtro de fecha */}
            {hasDateFilter && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                📅 {formatDate(new Date(dateFilters.startDate))} -{" "}
                {formatDate(new Date(dateFilters.endDate))}
                <button
                  onClick={() => {
                    setDateFilters({ startDate: null, endDate: null });
                    setDateRange({ startDate: null, endDate: null });
                    setHasAppliedFilter(false);
                  }}
                  className="ml-2 hover:text-green-600"
                >
                  ×
                </button>
              </span>
            )}

            {/* Filtros de crossfilter */}
            {activeFiltersList.map(([key, value]) => {
              const [filterType, tipo] = key.split("_");
              let label = "";

              if (filterType === "cumplimiento") {
                const cumpleText =
                  value === "cumple" ? "Cumple ✓" : "No Cumple ✗";
                label = `${tipo}: ${cumpleText}`;
              } else if (filterType === "tiempo") {
                const cumpleText =
                  value === "cumple" ? "Cumple ✓" : "No Cumple ✗";
                label = `Tiempo ${tipo}: ${cumpleText}`;
              } else if (filterType === "establecimiento") {
                label = `${tipo}: ${value}`;
              } else if (filterType === "mes") {
                label = `${tipo}: ${value}`;
              } else if (filterType === "ruta") {
                label = `${tipo}: ${value}`;
              } else if (filterType === "tipo_viaje") {
                label = `${tipo}: ${value}`;
              }

              return (
                <span
                  key={key}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {label}
                  <button
                    onClick={() =>
                      setActiveFilters((prev) => ({ ...prev, [key]: null }))
                    }
                    className="ml-2 hover:text-blue-600"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>

          <button
            onClick={() => {
              clearCrossFilters();
              setDateFilters({ startDate: null, endDate: null });
              setDateRange({ startDate: null, endDate: null });
              setHasAppliedFilter(false);
            }}
            className="text-sm text-blue-700 hover:text-blue-900 font-medium underline"
          >
            Limpiar todo
          </button>
        </div>
      </div>
    );
  };

  const DateRangePicker = () => {
    const displayText =
      hasAppliedFilter && dateRange.startDate && dateRange.endDate
        ? `${formatDate(dateRange.startDate)} - ${formatDate(
            dateRange.endDate
          )}`
        : "Filtrar por Rango de Fechas";

    return (
      <div
        className="bg-white rounded-lg shadow-sm border mb-4 relative"
        ref={datePickerRef}
      >
        <div
          onClick={() => setShowDatePicker(!showDatePicker)}
          className="flex items-center p-3 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
        >
          <Calendar className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 flex-1">
            {displayText}
          </span>
          {hasAppliedFilter && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearFilters();
              }}
              className="ml-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
              title="Limpiar filtros"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          )}
        </div>

        {showDatePicker && (
          <div className="absolute top-full left-0 mt-2 bg-white border rounded-lg shadow-xl z-50 animate-fadeIn flex">
            <div className="w-48 border-r bg-gray-50">
              <div className="p-2">
                <button
                  onClick={() => handleQuickRange("today")}
                  className="w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Hoy
                </button>
                <button
                  onClick={() => handleQuickRange("yesterday")}
                  className="w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Ayer
                </button>
                <button
                  onClick={() => handleQuickRange(7)}
                  className="w-full px-4 py-2.5 text-sm text-left text-blue-600 bg-blue-100 rounded-lg transition-colors font-medium"
                >
                  Últimos 7 días
                </button>
                <button
                  onClick={() => handleQuickRange(15)}
                  className="w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Últimos 15 días
                </button>
                <button
                  onClick={() => handleQuickRange(30)}
                  className="w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Últimos 30 días
                </button>
                <button
                  onClick={() => setShowCalendar(true)}
                  className="w-full px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
                >
                  Personalizado
                </button>
              </div>
            </div>

            {showCalendar && (
              <div className="flex flex-col">
                <div className="p-4">
                  <DatePicker
                    selectsRange={true}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setDateRange({ startDate: start, endDate: end });
                    }}
                    monthsShown={2}
                    inline
                    dateFormat="dd/MM/yyyy"
                  />
                </div>

                <div className="border-t p-4 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    {dateRange.startDate && dateRange.endDate
                      ? `${formatDate(dateRange.startDate)} - ${formatDate(
                          dateRange.endDate
                        )}`
                      : "Seleccione un rango de fechas"}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowDatePicker(false);
                        setShowCalendar(false);
                      }}
                      className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const ResumenCompacto = ({ resumen }) => {
    const formatearFecha = (fecha) => {
      if (!fecha) return "";
      const d = new Date(fecha);
      return d.toLocaleDateString("es-PE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    const fechaDesde = formatearFecha(resumen.rangoFechas?.desde);
    const fechaHasta = formatearFecha(resumen.rangoFechas?.hasta);

    return (
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-blue-900">
            Resumen General
          </h3>
          <Package className="h-4 w-4 text-blue-600" />
        </div>
        <div className="flex items-center justify-between text-center space-x-6">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-blue-900">
              {formatNumber(resumen.totalViajes)}
            </span>
            <span className="text-xs text-blue-700">Viajes</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-blue-700">Del</span>
            <span className="text-xs font-semibold text-blue-900">
              {fechaDesde}
            </span>
            <span className="text-xs text-blue-700">al</span>
            <span className="text-xs font-semibold text-blue-900">
              {fechaHasta}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const StatsCards = ({
    cumplimientoDeposito,
    cumplimientoPlanta,
    cumplimientoPuerto,
  }) => (
    <div className="grid grid-cols-3 gap-3 mt-4">
      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
        <div className="text-center">
          <p className="text-xl font-bold text-green-900">
            {cumplimientoDeposito.porcentajeCumple}%
          </p>
          <p className="text-xs text-green-700">Cumplimiento Depósito</p>
        </div>
      </div>
      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
        <div className="text-center">
          <p className="text-xl font-bold text-blue-900">
            {cumplimientoPlanta.porcentajeCumple}%
          </p>
          <p className="text-xs text-blue-700">Cumplimiento Planta</p>
        </div>
      </div>
      <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
        <div className="text-center">
          <p className="text-xl font-bold text-purple-900">
            {cumplimientoPuerto.porcentajeCumple}%
          </p>
          <p className="text-xs text-purple-700">Cumplimiento Puerto</p>
        </div>
      </div>
    </div>
  );

  const TiempoEstandarStatsCards = ({
    cumplimientoTiempoDeposito,
    cumplimientoTiempoPlanta,
    cumplimientoTiempoPuerto,
  }) => (
    <div className="grid grid-cols-3 gap-3 mt-4">
      <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
        <div className="text-center">
          <p className="text-xl font-bold text-emerald-900">
            {cumplimientoTiempoDeposito.porcentajeCumple}%
          </p>
          <p className="text-xs text-emerald-700">Tiempo Estándar Depósito</p>
        </div>
      </div>
      <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
        <div className="text-center">
          <p className="text-xl font-bold text-cyan-900">
            {cumplimientoTiempoPlanta.porcentajeCumple}%
          </p>
          <p className="text-xs text-cyan-700">Tiempo Estándar Planta</p>
        </div>
      </div>
      <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
        <div className="text-center">
          <p className="text-xl font-bold text-indigo-900">
            {cumplimientoTiempoPuerto.porcentajeCumple}%
          </p>
          <p className="text-xs text-indigo-700">Tiempo Estándar Puerto</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos del dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-900 mb-2">
          Error al cargar datos
        </h3>
        <p className="text-red-700 mb-4">{error}</p>
        <button
          onClick={() => fetchDashboardData()}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">
          Sin datos disponibles
        </h3>
        <p className="text-yellow-700 mb-4">
          No se encontraron datos de viajes para su usuario.
        </p>
        <button
          onClick={() => fetchDashboardData()}
          className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
        >
          Recargar
        </button>
      </div>
    );
  }

  const {
    cumplimientoDeposito,
    cumplimientoPlanta,
    cumplimientoPuerto,
    cumplimientoTiempoDeposito,
    cumplimientoTiempoPlanta,
    cumplimientoTiempoPuerto,
    resumenGeneral,
  } = dashboardData;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Dashboard Analítico
        </h1>
        <button
          onClick={() => fetchDashboardData()}
          className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Actualizar
        </button>
      </div>

      <DateRangePicker />
      <ActiveFiltersIndicator />
      <ResumenCompacto resumen={resumenGeneral} />

      {/* SECCIÓN 1: CUMPLIMIENTO DE LLEGADA A CITA */}
      <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
        <div className="flex items-center space-x-2 mb-4">
          <Target className="h-5 w-5 text-green-600" />
          <h2 className="text-lg font-bold text-green-900">
            Cumplimiento de Llegada a Cita
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PieChartWithLegend
            data={preparePieData(cumplimientoDeposito)}
            title={cumplimientoDeposito.titulo}
            total={cumplimientoDeposito.cumple + cumplimientoDeposito.noCumple}
            tipo="deposito"
            seccion="cumplimiento"
            icon={Target}
          />
          <PieChartWithLegend
            data={preparePieData(cumplimientoPlanta)}
            title={cumplimientoPlanta.titulo}
            total={cumplimientoPlanta.cumple + cumplimientoPlanta.noCumple}
            tipo="planta"
            seccion="cumplimiento"
            icon={Target}
          />
          <PieChartWithLegend
            data={preparePieData(cumplimientoPuerto)}
            title={cumplimientoPuerto.titulo}
            total={cumplimientoPuerto.cumple + cumplimientoPuerto.noCumple}
            tipo="puerto"
            seccion="cumplimiento"
            icon={Target}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <BarChartCard
            establecimientos={
              cumplimientoDeposito.cumplimientoPorEstablecimiento
            }
            tipo="deposito"
            seccion="cumplimiento"
            isTracto={false}
          />
          <BarChartCard
            establecimientos={cumplimientoPlanta.cumplimientoPorEstablecimiento}
            tipo="planta"
            seccion="cumplimiento"
            isTracto={false}
          />
          <BarChartCard
            establecimientos={cumplimientoPuerto.cumplimientoPorEstablecimiento}
            tipo="puerto"
            seccion="cumplimiento"
            isTracto={false}
          />
        </div>

        <StatsCards
          cumplimientoDeposito={cumplimientoDeposito}
          cumplimientoPlanta={cumplimientoPlanta}
          cumplimientoPuerto={cumplimientoPuerto}
        />
      </div>

      {/* SECCIÓN 2: CUMPLIMIENTO POR TIEMPO ESTÁNDAR */}
      {cumplimientoTiempoDeposito &&
        cumplimientoTiempoPlanta &&
        cumplimientoTiempoPuerto && (
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center space-x-2 mb-4">
              <Timer className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-bold text-purple-900">
                Cumplimiento de tiempo de atención
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <PieChartWithLegend
                data={preparePieData(cumplimientoTiempoDeposito)}
                title={`${cumplimientoTiempoDeposito.titulo} (≤ ${TIEMPOS_LIMITE.deposito} h.)`}
                total={cumplimientoTiempoDeposito.totalViajes}
                tipo="deposito"
                seccion="tiempo"
                icon={Timer}
              />
              <PieChartWithLegend
                data={preparePieData(cumplimientoTiempoPlanta)}
                title={`${cumplimientoTiempoPlanta.titulo} (≤ ${TIEMPOS_LIMITE.planta} h.)`}
                total={cumplimientoTiempoPlanta.totalViajes}
                tipo="planta"
                seccion="tiempo"
                icon={Timer}
              />
              <PieChartWithLegend
                data={preparePieData(cumplimientoTiempoPuerto)}
                title={`${cumplimientoTiempoPuerto.titulo} (≤ ${TIEMPOS_LIMITE.puerto} h.)`}
                total={cumplimientoTiempoPuerto.totalViajes}
                tipo="puerto"
                seccion="tiempo"
                icon={Timer}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <BarChartCard
                establecimientos={
                  cumplimientoTiempoDeposito.cumplimientoPorEstablecimiento
                }
                tipo="deposito"
                seccion="tiempo"
                isTracto={false}
              />
              <BarChartCard
                establecimientos={
                  cumplimientoTiempoPlanta.cumplimientoPorEstablecimiento
                }
                tipo="planta"
                seccion="tiempo"
                isTracto={false}
              />
              <BarChartCard
                establecimientos={
                  cumplimientoTiempoPuerto.cumplimientoPorEstablecimiento
                }
                tipo="puerto"
                seccion="tiempo"
                isTracto={false}
              />
            </div>

            <TiempoEstandarStatsCards
              cumplimientoTiempoDeposito={cumplimientoTiempoDeposito}
              cumplimientoTiempoPlanta={cumplimientoTiempoPlanta}
              cumplimientoTiempoPuerto={cumplimientoTiempoPuerto}
            />
          </div>
        )}
      {/* NUEVA SECCIÓN DE GRÁFICOS ADICIONALES */}
      <DashboardExtraCharts
        user={user}
        data={rawViajes}
        handleCrossFilter={handleCrossFilter}
        activeFilters={activeFilters}
        dateFilters={dateFilters}
        applyDateFilter={applyDateFilter}
        applyActiveFilters={applyActiveFilters}
        TIEMPOS_LIMITE={TIEMPOS_LIMITE}
      />
    </div>
  );
};

export default DashboardCharts;
